import React, { useEffect, useState } from "react";
import useSound from "use-sound";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
// COMPONENTS
import RapidFireHowToPlay from "Components/History/HowToPlay/HowToPlay";
import PresentOrPastHowToPlay from "Components/History/BillOrNoBill/HowToPlay/HowToPlay";
import LazyImage from "Components/Commons/LazyImage/LazyImage";
import GameLoader from "Components/Commons/GameLoader";
// STYLES
import { PageWrapper, GridContainer, ScoreboardWrapper, ProgressWrapper } from "./Dashboard.style";
// HELPERS
import { random, Api, URL_RAPID_FIRE, HISTORY_MODULE_ID, URL_PRESENT_OR_PAST, IMAGES } from "Helpers";
// REDUX
import { quizSound, showToast } from "Redux/General/Actions";
import { updateTotalScore, schoolClassTestID } from "Redux/Auth/Actions";

// SOUNDS
import DrawerSound from "Assets/Sounds/switch-off.mp3";

const tooltipStyles = { tooltip: { padding: "8px 18px", borderRadius: "10px", marginBottom: "22px", fontSize: "14px", backgroundColor: "white", color: "black" } };

const sectionConst = [
	{ name: "Rapid-fire", image: IMAGES.HISTORY.RAPID_FIRE_CARD, value: 0, subTitle: "RAPID FIRE", url: URL_RAPID_FIRE, id: HISTORY_MODULE_ID.RAPID_FIRE, isLive: true, isTestAvailable: false },
	{
		name: "Present or Past",
		image: IMAGES.HISTORY.PRESENT_OR_PAST_CARD,
		value: 0,
		subTitle: "BILL OR NO BILL",
		url: URL_PRESENT_OR_PAST,
		id: HISTORY_MODULE_ID.BILL_OR_NO_BILL,
		isLive: true,
		isTestAvailable: false,
	},
];

const Dashboard = () => {
	const dispatch = useDispatch();
	const [play] = useSound(DrawerSound, { volume: 0.2 });

	const { gamesStatus } = useSelector((state) => state.GamesData);
	const subjects = useSelector((state) => state.Auth.subjectAccessList);

	const subjectId = subjects?.filter((item) => item.subject === "History")[0].subjectId;

	const [sections, setSections] = useState([...sectionConst]);
	const [rapidFireHowToPlay, setRapidFireHowToPlay] = useState({ open: false, subTitle: "", url: null, id: null });
	const [presentOrPastHowToPlay, setPresentOrPastHowToPlay] = useState({ open: false, subTitle: "", url: null, id: null });
	const [scoreboard, setScoreboard] = useState([]);
	const [isStudent, setStudent] = useState(false);
	const [testAvailable, setTestStatus] = useState(false);
	const [status, setStatus] = useState(0);
	const [schoolId, setSchoolId] = useState("");
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		dispatch(quizSound(true));

		setTimeout(() => {
			let newValues = [...sections];

			newValues.forEach((section) => {
				if (!section.isLive) section.value = random(30, 100);
			});

			setSections(newValues);
		}, 1500);
	}, []); // eslint-disable-line

	useEffect(() => {
		if (Object.keys(gamesStatus).length) getStatus();
	}, [gamesStatus]); // eslint-disable-line

	const getStatus = async () => {
		try {
			setLoading(true);

			let response = await new Api().get("general/studentStatus", { params: { subjectId: subjectId } });

			const percentValue = (sub, total) => ((sub * 100) / total).toFixed(0);

			// SET PROGRESS
			const newValues = [...sections];

			newValues.forEach((section) => {
				switch (section.id) {
					case HISTORY_MODULE_ID.RAPID_FIRE:
						if (response?.data?.rapidFireHistory?.isRapidFireHistoryComplete) section.value = parseInt(100);
						else {
							let value = percentValue(response?.data?.rapidFireHistory?.totalRightAnswerRapidFireHistory, response?.data?.rapidFireHistory?.totalQuestionRapidFireHistory);
							section.value = !isNaN(value) ? value : 0;
						}

						if (gamesStatus?.isRapidFireHistoryActive) section.isTestAvailable = true;

						break;

					case HISTORY_MODULE_ID.BILL_OR_NO_BILL:
						if (response?.data?.billNoBillHistory.isBillNoBillComplete) section.value = parseInt(100);
						else {
							let value = percentValue(response?.data?.billNoBillHistory?.totalRightAnswerBillNoBillHistory, response?.data?.billNoBillHistory?.totalQuestionBillNoBillHistory);
							section.value = !isNaN(value) ? value : 0;
						}
						if (gamesStatus?.isHistoryBillNoBillActive) {
							section.isTestAvailable = true;
						}
						break;

					default:
						section.value = 0;
						break;
				}
			});

			dispatch(updateTotalScore(response.data.total));
			dispatch(schoolClassTestID(response?.data?.schoolClassTestId));

			setSections(newValues);
			setScoreboard(response.data.scoreDashBoardList);
			setTestStatus(response?.data?.isLiveTestAvailable);
			setStudent(response?.data?.isStudentLogin);
			setStatus(response.data.status);
			setSchoolId(response?.data?.schoolClassTestId);
		} catch (err) {
			// Keep dashboard usable even if status fetch fails
		} finally {
			setLoading(false);
		}
	};

	const toggleHowToPlay = (section) => {
		if (!testAvailable && isStudent) {
			dispatch(showToast("Assessment is not available. Try after some time or contact to your respective class teacher | school."));
			return;
		}

		if (status !== 0) {
			let setStateFunction;

			if (section.url === URL_RAPID_FIRE) setStateFunction = setRapidFireHowToPlay;
			if (section.url === URL_PRESENT_OR_PAST) setStateFunction = setPresentOrPastHowToPlay;

			if (setStateFunction) {
				setStateFunction((prev) => {
					const copyPrev = { ...prev };

					if (!copyPrev.open) {
						copyPrev.url = section.url;
						copyPrev.subTitle = section.subTitle;
						copyPrev.id = section.id;
					}

					copyPrev.open = !copyPrev.open;

					return copyPrev;
				});
			}
		} else {
			dispatch(showToast("No Active study guide found."));
		}
	};

	const CustomTooltip = withStyles(tooltipStyles)(Tooltip);

	const gameListUi = () => {
		return (
			<GridContainer className="flex f-v-center f-h-center overlay-effect">
				{sections.map((section) =>
					section.isLive ? (
						<div className="box" key={section.name}>
							<div className={`box-content `} onClick={() => !loading && section.isTestAvailable && toggleHowToPlay(section)}>
								<div className={`img-container ${!section.isTestAvailable && "disabled"}`} style={{ backgroundImage: `url(${section.image})` }} onMouseOver={play}></div>

								<div className="progress-container full-width" onMouseOver={(e) => e.preventDefault()}>
									<Progress value={section.value} />
								</div>
							</div>
						</div>
					) : (
						<CustomTooltip title="Coming soon!!!" placement="top">
							<div className="box" key={section.name}>
								<div className={`box-content `}>
									<div className="img-container" style={{ backgroundImage: `url(${section.image})` }} onMouseOver={play}></div>
								</div>
							</div>
						</CustomTooltip>
					)
				)}
			</GridContainer>
		);
	};

	if (loading) {
		return (
			<GameLoader
				compressedImage={IMAGES.HISTORY.COMPRESSED_COMMON_BACKGROUND}
				image={IMAGES.HISTORY.COMMON_BACKGROUND}
				message="Loading..."
			/>
		);
	}

	return (
		<PageWrapper>
			<LazyImage compressedImage={IMAGES.HISTORY.COMPRESSED_COMMON_BACKGROUND} image={IMAGES.HISTORY.COMMON_BACKGROUND} wrapperProps={{ className: "background-image" }} />

			<div className="title-history">Understanding Our Past Will Guide Our Future</div>
			<div className="content">{gameListUi()}</div>

			<Scoreboard peoples={scoreboard} />

			{rapidFireHowToPlay.open && <RapidFireHowToPlay open={rapidFireHowToPlay.open} handleClose={toggleHowToPlay} data={rapidFireHowToPlay} subjectId={subjectId} schoolId={schoolId} />}

			{presentOrPastHowToPlay.open && (
				<PresentOrPastHowToPlay open={presentOrPastHowToPlay.open} handleClose={toggleHowToPlay} data={presentOrPastHowToPlay} subjectId={subjectId} schoolId={schoolId} />
			)}
		</PageWrapper>
	);
};

function Progress(props) {
	return (
		<ProgressWrapper {...props}>
			<div className="outer">
				<div className="active" />
			</div>
			<div className="text">{props.value}%</div>
		</ProgressWrapper>
	);
}

function Scoreboard(props) {
	return (
		<ScoreboardWrapper>
			<div className="scoreboard-title">Scoreboard</div>
			<div className="people-cards">
				{props?.peoples?.map((person) => (
					<div className="person flex f-v-center f-h-center" key={person.winnerPlace}>
						<div className="content text-center">
							<div className="image" style={{ backgroundImage: `url(${person.profileImage})` }}></div>
							<div className="name elipsis">{person.firstName}</div>
							<div className="score">{person.totalScore}</div>
						</div>
					</div>
				))}
			</div>
		</ScoreboardWrapper>
	);
}

export default Dashboard;
